import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Paper,
} from '@mui/material';
import useComponentStyles from '../theme/useComponentStyles';

/**
 * Example component that demonstrates how to use the component styles system
 * This serves as a reference for developers to understand how to apply consistent styling
 */
function StylesExample() {
  const styles = useComponentStyles();

  return (
    <Box sx={styles.containers.pageContainer}>
      <Typography variant="h4" gutterBottom sx={styles.text.sectionTitle}>
        Idotoo Styles Reference
      </Typography>
      <Typography variant="body1" paragraph sx={styles.text.sectionDescription}>
        This page demonstrates the available component styles and how to use them consistently across the application.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Card Styles */}
      <Typography variant="h5" gutterBottom sx={styles.text.sectionTitle}>
        Card Styles
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={styles.cards.default}>
            <CardContent>
              <Typography variant="h6" sx={styles.text.cardTitle}>
                Default Card
              </Typography>
              <Typography variant="body2" sx={styles.text.cardSubtitle}>
                No shadow, just a subtle border (preferred style)
              </Typography>
              <Typography variant="code" sx={{ mt: 2, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                sx=&#123;styles.cards.default&#125;
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={styles.cards.elevated}>
            <CardContent>
              <Typography variant="h6" sx={styles.text.cardTitle}>
                Elevated Card
              </Typography>
              <Typography variant="body2" sx={styles.text.cardSubtitle}>
                Card with shadow effect (use only when needed)
              </Typography>
              <Typography variant="code" sx={{ mt: 2, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                sx=&#123;styles.cards.elevated&#125;
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={styles.cards.flat}>
            <CardContent>
              <Typography variant="h6" sx={styles.text.cardTitle}>
                Flat Card
              </Typography>
              <Typography variant="body2" sx={styles.text.cardSubtitle}>
                Same as default (maintained for compatibility)
              </Typography>
              <Typography variant="code" sx={{ mt: 2, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                sx=&#123;styles.cards.flat&#125;
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={styles.cards.gradient}>
            <CardContent>
              <Typography variant="h6" sx={styles.text.cardTitle}>
                Gradient Card
              </Typography>
              <Typography variant="body2" sx={styles.text.cardSubtitle}>
                Card with gradient background
              </Typography>
              <Typography variant="code" sx={{ mt: 2, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                sx=&#123;styles.cards.gradient&#125;
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={styles.cards.accent}>
            <CardContent>
              <Typography variant="h6" sx={styles.text.cardTitle}>
                Accent Card
              </Typography>
              <Typography variant="body2" sx={styles.text.cardSubtitle}>
                Card with primary color accent
              </Typography>
              <Typography variant="code" sx={{ mt: 2, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                sx=&#123;styles.cards.accent&#125;
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={styles.cards.feature}>
            <CardContent>
              <Typography variant="h6" sx={styles.text.cardTitle}>
                Feature Card
              </Typography>
              <Typography variant="body2" sx={styles.text.cardSubtitle}>
                Card for highlighting features
              </Typography>
              <Typography variant="code" sx={{ mt: 2, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                sx=&#123;styles.cards.feature&#125;
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={styles.cards.dashboardSection}>
            <CardContent>
              <Typography variant="h6" sx={styles.text.cardTitle}>
                Dashboard Section Card
              </Typography>
              <Typography variant="body2" sx={styles.text.cardSubtitle}>
                Card for dashboard sections
              </Typography>
              <Typography variant="code" sx={{ mt: 2, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                sx=&#123;styles.cards.dashboardSection&#125;
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Text Styles */}
      <Typography variant="h5" gutterBottom sx={styles.text.sectionTitle}>
        Text Styles
      </Typography>
      
      <Paper sx={{ p: 3, mb: 6, ...styles.cards.flat }}>
        <Typography variant="h4" gutterBottom sx={styles.text.sectionTitle}>
          Section Title (styles.text.sectionTitle)
        </Typography>
        
        <Typography variant="body1" paragraph sx={styles.text.sectionDescription}>
          Section Description - This is the standard description text used for section introductions.
          (styles.text.sectionDescription)
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={styles.text.cardTitle}>
          Card Title (styles.text.cardTitle)
        </Typography>
        
        <Typography variant="body2" paragraph sx={styles.text.cardSubtitle}>
          Card Subtitle - This is used for secondary information in cards. (styles.text.cardSubtitle)
        </Typography>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Button Styles */}
      <Typography variant="h5" gutterBottom sx={styles.text.sectionTitle}>
        Button Styles
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 6 }}>
        <Button variant="contained" sx={styles.buttons.primary}>
          Primary Button
        </Button>
        
        <Button variant="contained" color="secondary" sx={styles.buttons.primary}>
          Secondary Button
        </Button>
        
        <Button variant="outlined" sx={styles.buttons.primary}>
          Outlined Button
        </Button>
        
        <Button variant="contained" sx={styles.buttons.subtle}>
          Subtle Button
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Layout Styles */}
      <Typography variant="h5" gutterBottom sx={styles.text.sectionTitle}>
        Layout Styles
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, ...styles.cards.flat }}>
            <Typography variant="subtitle1" gutterBottom>
              flexBetween (styles.layout.flexBetween)
            </Typography>
            <Box sx={{ ...styles.layout.flexBetween, p: 2, bgcolor: 'rgba(0,0,0,0.05)' }}>
              <Typography>Left content</Typography>
              <Typography>Right content</Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, ...styles.cards.flat }}>
            <Typography variant="subtitle1" gutterBottom>
              flexCenter (styles.layout.flexCenter)
            </Typography>
            <Box sx={{ ...styles.layout.flexCenter, p: 2, height: 100, bgcolor: 'rgba(0,0,0,0.05)' }}>
              <Typography>Centred content</Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, ...styles.cards.flat }}>
            <Typography variant="subtitle1" gutterBottom>
              flexColumn (styles.layout.flexColumn)
            </Typography>
            <Box sx={{ ...styles.layout.flexColumn, p: 2, gap: 1, bgcolor: 'rgba(0,0,0,0.05)' }}>
              <Typography>First item</Typography>
              <Typography>Second item</Typography>
              <Typography>Third item</Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, ...styles.cards.flat }}>
            <Typography variant="subtitle1" gutterBottom>
              Grid (styles.layout.grid)
            </Typography>
            <Box sx={{ ...styles.layout.grid, p: 2 }}>
              <Box sx={{ bgcolor: 'rgba(0,0,0,0.05)', p: 2, textAlign: 'center' }}>1</Box>
              <Box sx={{ bgcolor: 'rgba(0,0,0,0.05)', p: 2, textAlign: 'center' }}>2</Box>
              <Box sx={{ bgcolor: 'rgba(0,0,0,0.05)', p: 2, textAlign: 'center' }}>3</Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Usage Instructions */}
      <Card sx={styles.cards.feature}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={styles.text.sectionTitle}>
            How to Use Component Styles
          </Typography>
          
          <Typography variant="body1" paragraph>
            To use these styles in your component:
          </Typography>
          
          <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
            <pre style={{ margin: 0, overflow: 'auto' }}>
              {`import useComponentStyles from '../theme/useComponentStyles';

function YourComponent() {
  const styles = useComponentStyles();
  
  return (
    <Card sx={styles.cards.gradient}>
      <CardContent>
        <Typography sx={styles.text.cardTitle}>
          Your Title
        </Typography>
      </CardContent>
    </Card>
  );
}`}
            </pre>
          </Box>
          
          <Typography variant="body1">
            You can also combine styles or override specific properties:
          </Typography>
          
          <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, fontFamily: 'monospace', mt: 2 }}>
            <pre style={{ margin: 0, overflow: 'auto' }}>
              {`<Box sx={{
  ...styles.layout.flexBetween,
  p: 3, // Add padding
  bgcolor: 'primary.light', // Override background color
}}>
  Content here
</Box>`}
            </pre>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default StylesExample; 